/**
 * IssueTabsSettingsSection - Show/hide the tabs on the Production Issues page.
 *
 * A per-user (localStorage) view preference. Presentational only: the parent
 * (ProductionSettingsPage) owns the single `useProductionSettings` instance and
 * passes the current hidden set + a toggle callback, so this never spins up a
 * second localStorage instance that could clobber sibling settings.
 *
 * The last remaining visible tab's switch is disabled (with an always-visible,
 * aria-linked hint) so a user can never hide every tab.
 */
import { useTranslation } from 'react-i18next';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { ISSUE_TABS } from '@/features/production-issues/issueTabs';
import type { TabValue } from '@/features/production-issues/types';

interface IssueTabsSettingsSectionProps {
  /** Tab values currently hidden (denylist). */
  hiddenTabs: TabValue[];
  /** Toggle a tab's visibility. `visible=false` hides it, `true` shows it. */
  onToggleTab: (tab: TabValue, visible: boolean) => void;
}

export function IssueTabsSettingsSection({
  hiddenTabs,
  onToggleTab,
}: IssueTabsSettingsSectionProps) {
  const { t } = useTranslation('production');

  const visibleCount = ISSUE_TABS.filter((tab) => !hiddenTabs.includes(tab.value)).length;

  return (
    <div className="space-y-4">
      <div className="space-y-1">
        <h3 className="text-base font-medium">{t('issues.tabVisibility.heading')}</h3>
        <p className="text-sm text-muted-foreground">
          {t('issues.tabVisibility.description')}
        </p>
      </div>

      <div className="space-y-3">
        {ISSUE_TABS.map((tab) => {
          const Icon = tab.icon;
          const checked = !hiddenTabs.includes(tab.value);
          // Can't hide the last visible tab.
          const lockedOn = checked && visibleCount <= 1;
          const switchId = `tab-vis-${tab.value}`;
          const hintId = `${switchId}-hint`;

          return (
            <div key={tab.value} className="flex flex-col gap-1">
              <div className="flex items-center justify-between gap-3">
                <Label
                  htmlFor={switchId}
                  className="flex items-center gap-2 text-sm font-normal"
                >
                  <Icon className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
                  {t(tab.labelKey)}
                </Label>
                <Switch
                  id={switchId}
                  checked={checked}
                  disabled={lockedOn}
                  aria-describedby={lockedOn ? hintId : undefined}
                  onCheckedChange={(next) => onToggleTab(tab.value, next)}
                />
              </div>
              {lockedOn && (
                <p id={hintId} className="text-xs text-muted-foreground">
                  {t('issues.tabVisibility.lastTabHint')}
                </p>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
